import { useState, useEffect, useRef, useCallback } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiClient } from '@/utils/api-client';
import markdownRenderers from '@/utils/markdown-renderers';
import { skillTemplateService, type SkillTemplate } from '@/services/skill-template-service';
import {
  Header,
  ContentContainer,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { Send, ChevronLeft, ChevronRight, RefreshCw } from '@/lib/icons';

interface ContextResultItem {
  title: string | null;
  url: string | null;
  relevance: number | null;
  content_preview: string | null;
  is_golden?: boolean;
  domain?: string | null;
  program_name?: string | null;
  unit_title?: string | null;
  hub_type?: string | null;
  content_type?: string | null;
}

const CONTENT_TYPE_LABELS: Record<string, string> = {
  Content: 'Additional AI Content',
};

// Which account's entitlements scoped this turn's retrieval — the same
// descriptor eval runs record, so playground output stays comparable to scores.
export interface RanAs {
  user_id: number;
  email: string | null;
  account_id: number | null;
  account_name: string | null;
}

interface PlaygroundDebug {
  ran_as?: RanAs | null;
  confidence: number | null;
  matched_via: string | null;
  skill_name: string | null;
  executor: string | null;
  domain_slugs?: string[];
  project_focused?: boolean | null;
  links_included?: boolean;
  context_results_count: number;
  duration_ms: number;
  context_results?: ContextResultItem[];
  thinking_content?: string | null;
}

interface PlaygroundResponse {
  status: 'processing' | 'completed' | 'failed';
  response: string | null;
  thinking_content?: string | null;
  pending_options?: { question: string; options: { id: string; label: string; description?: string }[] } | null;
  error?: string;
  debug: PlaygroundDebug;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  thinkingContent?: string | null;
  options?: PlaygroundResponse['pending_options'];
}

const POLL_INTERVAL_MS = 2000;

export function KaiyaPlaygroundPage() {
  const { setShowPageHeader } = usePageContext();
  const [message, setMessage] = useState('');
  const [skillOverride, setSkillOverride] = useState('');
  const [topK, setTopK] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Synchronous lock guarding against a rapid double-submit creating two
  // sessions: the isProcessing state update is async, so two taps can both
  // clear the guard before the first re-render. A ref flips immediately.
  const processingLockRef = useRef(false);
  // Guards against in-flight poll responses landing after New Chat or unmount:
  // clearing the timer doesn't cancel a request that's already awaiting.
  const activeRequestIdRef = useRef<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Multi-turn state
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [debugEntries, setDebugEntries] = useState<PlaygroundDebug[]>([]);
  const [activeDebugIndex, setActiveDebugIndex] = useState(0);

  useEffect(() => {
    setShowPageHeader(false);
    return () => {
      setShowPageHeader(true);
      activeRequestIdRef.current = null;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [setShowPageHeader]);

  // Auto-scroll chat pane to bottom (without moving the page)
  useEffect(() => {
    const el = chatContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, isProcessing]);

  const { data: skillsData } = useQuery({
    queryKey: ['skillTemplates', 'active'],
    queryFn: () => skillTemplateService.list({ active: 'true' }),
  });

  const skills: SkillTemplate[] =
    extractAttributesList(skillsData);

  const ensureSession = async (): Promise<number> => {
    if (sessionId) return sessionId;
    const data = await apiClient.post<{ session_id: number }>('/api/admin/playground/create_session');
    setSessionId(data.session_id);
    return data.session_id;
  };

  const pollForResult = useCallback((requestId: string) => {
    activeRequestIdRef.current = requestId;
    const poll = async () => {
      try {
        const data = await apiClient.get<PlaygroundResponse>(
          '/api/admin/playground/result',
          { request_id: requestId }
        );

        // Stale resolution: New Chat, a newer send, or unmount superseded
        // this request while the fetch was in flight.
        if (activeRequestIdRef.current !== requestId) return;

        if (data.status === 'processing') {
          pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          const result = data;

          // Strip [present_options] prefix from displayed text
          let responseText = result.response || '';
          if (responseText.startsWith('[present_options]')) {
            responseText = '';
          }

          // Add assistant message
          const assistantMsg: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: responseText,
            thinkingContent: result.thinking_content || null,
            options: result.pending_options || null,
          };
          setMessages((prev) => [...prev, assistantMsg]);

          // Store debug entry — always add one per turn for paging
          const debug = {
            ...(result.debug || { confidence: null, matched_via: null, skill_name: null, executor: null, context_results_count: 0, duration_ms: 0 }),
            thinking_content: result.thinking_content || null,
          };
          setDebugEntries((prev) => {
            const next = [...prev, debug];
            setActiveDebugIndex(next.length - 1);
            return next;
          });

          activeRequestIdRef.current = null;
          processingLockRef.current = false;
          setIsProcessing(false);
        }
      } catch {
        if (activeRequestIdRef.current !== requestId) return;
        activeRequestIdRef.current = null;
        processingLockRef.current = false;
        toast.error('Failed to fetch result');
        setIsProcessing(false);
      }
    };
    poll();
  }, []);

  const handleSend = async (overrideMessage?: string) => {
    const text = (overrideMessage || message).trim();
    if (!text || processingLockRef.current) return;
    processingLockRef.current = true;

    const isFollowUp = messages.length > 0;
    setIsProcessing(true);
    if (!overrideMessage) setMessage('');

    // Add user message to thread
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: text }]);

    try {
      const sid = await ensureSession();

      const params: Record<string, unknown> = {
        message: text,
        chat_session_id: sid,
        follow_up: isFollowUp,
      };
      if (skillOverride) params.skill_override = skillOverride;
      if (topK) params.top_k = parseInt(topK);

      const { request_id } = await apiClient.post<{ request_id: string }>(
        '/api/admin/playground/test',
        params
      );
      pollForResult(request_id);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Request failed');
      processingLockRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleNewChat = async () => {
    if (sessionId) {
      try {
        await apiClient.delete('/api/admin/playground/clear_session', { session_id: sessionId });
      } catch {
        // Ignore cleanup errors
      }
    }
    setSessionId(null);
    setMessages([]);
    setDebugEntries([]);
    setActiveDebugIndex(0);
    setMessage('');
    processingLockRef.current = false;
    setIsProcessing(false);
    activeRequestIdRef.current = null;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  };

  const handleOptionSelect = (option: { id: string; label: string; description?: string }) => {
    handleSend(option.label);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const currentDebug = debugEntries[activeDebugIndex] || null;
  const totalTurns = debugEntries.length;

  return (
    <>
      <Header title="Kaiya Playground" description="Test Kaiya's AI pipeline with multi-turn conversations and debug information" />

      <ContentContainer>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ minHeight: '70vh' }}>
          {/* Left: Chat thread */}
          <div className="lg:col-span-2 flex flex-col">
            {/* Config bar */}
            <Card className="mb-4">
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <Select value={skillOverride || 'auto'} onValueChange={(v) => setSkillOverride(v === 'auto' ? '' : v)}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Auto-route" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-route</SelectItem>
                      {skills.map((s) => (
                        <SelectItem key={s.id} value={s.name}>
                          {s.display_title || s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input type="number" className="px-3 py-2 border rounded-md text-sm w-[100px]" placeholder="top_k" value={topK} onChange={(e) => setTopK(e.target.value)} min={1} max={20} />
                  <div className="flex-1" />
                  {messages.length > 0 && (
                    <Button variant="outline" size="sm" onClick={handleNewChat}>
                      <RefreshCw className="h-3 w-3 mr-1.5" />
                      New Chat
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chat messages */}
            <Card className="flex-1 flex flex-col">
              <CardContent ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '55vh' }}>
                {messages.length === 0 && !isProcessing && <div className="flex items-center justify-center h-full text-sm text-muted-foreground">Send a message to start testing</div>}

                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`rounded-lg px-4 py-3 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      {msg.role === 'assistant' ? (
                        <>
                          {msg.thinkingContent && (
                            <details className="mb-2">
                              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">View thinking</summary>
                              <div className="mt-1 text-xs text-muted-foreground italic border-l-2 border-muted-foreground/20 pl-2 max-h-32 overflow-y-auto">
                                {msg.thinkingContent.slice(0, 500)}
                                {msg.thinkingContent.length > 500 ? '...' : ''}
                              </div>
                            </details>
                          )}
                          <div className="prose prose-sm max-w-none [&_*]:text-sm [&_*]:leading-relaxed [&_*:last-child]:mb-0 [&_ol]:ml-6 [&_ul]:ml-6 [&_ol]:list-decimal [&_ul]:list-disc [&_pre]:whitespace-pre-wrap">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownRenderers}>
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                          {msg.options && msg.options.options.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <p className="text-xs font-medium text-muted-foreground">{msg.options.question}</p>
                              <div className="flex flex-wrap gap-2">
                                {msg.options.options.map((opt) => (
                                  <Button key={opt.id} variant="outline" size="sm" className="text-xs" onClick={() => handleOptionSelect(opt)} disabled={isProcessing}>
                                    {opt.label}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}

                {isProcessing && (
                  <div className="bg-muted rounded-lg px-4 py-3 inline-block">
                    <Spinner className="h-4 w-4" style={{ width: '100%', height: '70%' }} />
                  </div>
                )}
              </CardContent>

              {/* Input */}
              <div className="border-t p-3">
                <div className="flex gap-3">
                  <textarea className="flex-1 px-3 py-2 border rounded-md text-sm resize-none" rows={2} placeholder={messages.length > 0 ? 'Type a follow-up...' : 'Ask Kaiya something...'} value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={handleKeyDown} />
                  <Button onClick={() => handleSend()} disabled={!message.trim() || isProcessing} className="self-end">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Right: Debug panel */}
          <div className="space-y-4">
            {totalTurns > 0 ? (
              <>
                {/* Turn navigation */}
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setActiveDebugIndex((i) => Math.max(0, i - 1))} disabled={activeDebugIndex === 0}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium">
                    Turn {activeDebugIndex + 1} of {totalTurns}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setActiveDebugIndex((i) => Math.min(totalTurns - 1, i + 1))} disabled={activeDebugIndex === totalTurns - 1}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {currentDebug && (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Ran As</CardTitle>
                        <CardDescription className="text-xs">
                          Whose product entitlements scoped this turn's retrieval
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <DebugRow label="Account" value={currentDebug.ran_as?.account_name ?? null} />
                        <DebugRow label="User" value={currentDebug.ran_as?.email ?? null} />
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Skill Match</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <DebugRow label="Skill" value={currentDebug.skill_name} />
                        <DebugRow label="Matched Via" value={currentDebug.matched_via} />
                        <DebugRow label="Confidence" value={currentDebug.confidence != null ? `${(currentDebug.confidence * 100).toFixed(0)}%` : null} />
                        <DebugRow label="Executor" value={currentDebug.executor} />
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground shrink-0">Domains</span>
                          <div className="flex flex-wrap justify-end gap-1">
                            {currentDebug.domain_slugs?.length ? (
                              currentDebug.domain_slugs.map((slug) => (
                                <Badge key={slug} variant="outline" className="text-[10px]">
                                  {slug}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Performance</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <DebugRow label="Context Results" value={String(currentDebug.context_results_count)} />
                        {currentDebug.project_focused != null && <DebugRow label="Project-focused" value={String(currentDebug.project_focused)} />}
                        {currentDebug.links_included != null && <DebugRow label="Links Included" value={String(currentDebug.links_included)} />}
                        <DebugRow label="Duration" value={`${currentDebug.duration_ms}ms`} />
                      </CardContent>
                    </Card>

                    {/* Thinking content */}
                    {currentDebug.thinking_content && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Thinking</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-xs text-muted-foreground italic max-h-40 overflow-y-auto whitespace-pre-wrap">{currentDebug.thinking_content}</div>
                        </CardContent>
                      </Card>
                    )}

                    {currentDebug.context_results && currentDebug.context_results.length > 0 && <ContextResultsPanel results={currentDebug.context_results} />}
                  </>
                )}
              </>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Debug Info</CardTitle>
                  <CardDescription>Send a message to see debug information about Kaiya's processing pipeline.</CardDescription>
                </CardHeader>
              </Card>
            )}
          </div>
        </div>
      </ContentContainer>
    </>
  )
}

function DebugRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      {value ? (
        <Badge variant="outline" className="text-[10px]">{value}</Badge>
      ) : (
        <span className="text-muted-foreground">-</span>
      )}
    </div>
  );
}

function ContextResultsPanel({ results }: { results: ContextResultItem[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Context Sources</CardTitle>
        <CardDescription className="text-xs">
          {results.length} document{results.length !== 1 ? 's' : ''} retrieved
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {[...results]
          .sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0))
          .map((item, i) => (
          <div
            key={i}
            className={`border rounded-md p-2 space-y-1${item.is_golden ? ' border-amber-500/40 bg-amber-50 dark:bg-amber-950/30' : ''}`}
          >
            <div className="flex items-start justify-between gap-2">
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium leading-tight truncate text-primary hover:underline"
                >
                  {item.title || 'Untitled'}
                </a>
              ) : (
                <span className="text-xs font-medium leading-tight truncate">
                  {item.title || 'Untitled'}
                </span>
              )}
              <div className="flex items-center gap-1 shrink-0">
                {item.is_golden && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-500 text-amber-700">
                    Golden
                  </Badge>
                )}
                {item.relevance != null && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0">
                    {item.relevance.toFixed(1)}
                  </Badge>
                )}
              </div>
            </div>
            {item.program_name ? (
              <p className="text-[10px] text-muted-foreground truncate">
                {[item.hub_type, item.program_name, item.unit_title].filter(Boolean).join(' › ')}
              </p>
            ) : item.content_type ? (
              <Badge variant="secondary" className="text-[9px] px-1 py-0 font-normal">
                {CONTENT_TYPE_LABELS[item.content_type] ?? item.content_type}
              </Badge>
            ) : null}
            {item.content_preview && (
              <p className="text-[10px] text-muted-foreground line-clamp-2">
                {item.content_preview}
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
